import React, { useState, useEffect, useCallback } from 'react';
import ThemeToggle from './ThemeToggle';
import Logo from './Logo';
import './Auth.css';
import './PinLockOverlay.css';

const PinLockOverlay = ({ onUnlock, onLogout }) => {
  const [pin, setPin] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const overlayRef = React.useRef(null);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 768px)');
    setIsMobile(mq.matches);
    const handler = () => setIsMobile(mq.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  const applyThemeBackground = useCallback(() => {
    const theme = document.documentElement.getAttribute('data-theme') || 'dark';
    const publicUrl = process.env.PUBLIC_URL || '';
    const darkBg = `${publicUrl}/backgrounds/background-dark.png`;
    const lightBg = `${publicUrl}/backgrounds/background-light.png`;
    const bgUrl = theme === 'light' ? lightBg : darkBg;
    if (overlayRef.current) {
      overlayRef.current.style.backgroundImage = `url(${bgUrl})`;
      overlayRef.current.style.backgroundColor = theme === 'light' ? '#fafafa' : '#121212';
      overlayRef.current.style.backgroundSize = 'cover';
      overlayRef.current.style.backgroundPosition = 'center';
      overlayRef.current.style.backgroundRepeat = 'no-repeat';
      overlayRef.current.style.backgroundAttachment = 'fixed';
    }
  }, []);

  useEffect(() => {
    applyThemeBackground();
    const observer = new MutationObserver(() => {
      applyThemeBackground();
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme']
    });
    return () => observer.disconnect();
  }, [applyThemeBackground]);

  const pinStr = pin.replace(/\D/g, '');
  const addDigit = (d) => {
    if (pinStr.length >= 8) return;
    setPin(pinStr + d);
    setError('');
  };
  const removeDigit = () => {
    setPin(pinStr.slice(0, -1));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const pinStr = pin.replace(/\D/g, '');
    if (pinStr.length < 4 || pinStr.length > 8) {
      setError('Введите PIN из 4–8 цифр');
      return;
    }
    setError('');
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch('/api/auth/unlock-session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ pin: pinStr })
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.ok) {
        onUnlock();
        setPin('');
      } else {
        setError(data.error || 'Неверный PIN');
      }
    } catch (err) {
      setError('Ошибка сети');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div ref={overlayRef} className="pin-lock-overlay">
      <div className="auth-container pin-lock-auth-container">
        <ThemeToggle />
        <div className="auth-box">
          <div className="auth-left-panel">
            <div className="auth-header pin-lock-auth-header">
              <Logo variant="auth" style={{ marginBottom: '10px' }} />
              <h2 className="pin-lock-auth-title">Введите пин-код</h2>
              <p className="pin-lock-subtitle">Введите код доступа, чтобы продолжить работу.</p>
            </div>

            {error && (
              <div className="auth-error-message">
                <span className="auth-error-icon">⚠</span>
                <span className="auth-error-text">{error}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="auth-form pin-lock-form">
              {/* Десктоп: обычное поле ввода */}
              <div className="form-group pin-lock-input-wrap">
                <label htmlFor="pin-lock-input">PIN-код</label>
                <input
                  id="pin-lock-input"
                  type="password"
                  inputMode="numeric"
                  autoComplete="off"
                  pattern="[0-9]*"
                  placeholder="••••"
                  value={pin}
                  readOnly={isMobile}
                  onChange={(e) => {
                    setPin(e.target.value.replace(/\D/g, '').slice(0, 8));
                    setError('');
                  }}
                  maxLength={8}
                  className="pin-lock-input-auth"
                />
              </div>

              {/* Телефон: точки по мере ввода + клавиатура */}
              <div className="pin-lock-keypad-block">
                <div
                  className="pin-lock-dots"
                  role="status"
                  aria-live="polite"
                  aria-label={pinStr.length ? `Введено цифр: ${pinStr.length}` : 'Введите PIN-код'}
                >
                  {pinStr.length > 0 &&
                    [...Array(pinStr.length)].map((_, i) => (
                      <span key={i} className="pin-lock-dot" />
                    ))}
                </div>
                <div className="pin-lock-keypad">
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => (
                    <button
                      key={n}
                      type="button"
                      className="pin-lock-keypad-btn"
                      onClick={() => addDigit(String(n))}
                    >
                      {n}
                    </button>
                  ))}
                  {onLogout ? (
                    <button type="button" className="pin-lock-keypad-btn pin-lock-keypad-btn-logout" onClick={onLogout}>
                      Выйти
                    </button>
                  ) : (
                    <span className="pin-lock-keypad-btn pin-lock-keypad-btn-empty" aria-hidden="true" />
                  )}
                  <button
                    type="button"
                    className="pin-lock-keypad-btn"
                    onClick={() => addDigit('0')}
                  >
                    0
                  </button>
                  <button
                    type="button"
                    className="pin-lock-keypad-btn pin-lock-keypad-btn-back"
                    onClick={removeDigit}
                    title="Стереть"
                    aria-label="Стереть последнюю цифру"
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M20 5H9.5L3 12l6.5 7H20a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2z" />
                      <line x1="15.5" y1="9.5" x2="10.5" y2="14.5" />
                      <line x1="10.5" y1="9.5" x2="15.5" y2="14.5" />
                    </svg>
                  </button>
                </div>
              </div>

              <div className="pin-lock-actions">
                <button
                  type="submit"
                  className="auth-button"
                  disabled={loading || pinStr.length < 4}
                >
                  {loading ? 'Проверка...' : 'Войти'}
                </button>
                {onLogout && (
                  <button type="button" className="auth-button auth-button-secondary pin-lock-logout-btn" onClick={onLogout}>
                    Выйти из аккаунта
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PinLockOverlay;
